<template>
  <div class="viewing-appointments-tab">
    <coozzy-card class="global-card mb-3">
      <viewing-appointment-contact-section
        ref="viewingContactSection"
        :object="localObject"
        :building="building"
        :building-ready="buildingReady"
        :contact-id-assignee-marketing="contactIdAssigneeMarketing"
        :show-advert-fields="false"
        :lock-contact-selection="!appointmentsLoaded || (hasActiveAppointments && isViewingContactSelected)"
        :show-contact-lock-tooltip="appointmentsLoaded && hasActiveAppointments && isViewingContactSelected"
        @viewing-appointment-updated="onViewingAppointmentUpdated"
        @viewing-contact-updated="onViewingContactUpdated"
        @contact-selection-changed="onContactSelectionChanged"
        @contacts-resolved="onContactsResolved" />
      <div
        v-if="requestedAtFormatted"
        class="viewing-appointments-requested-note mt-3 px-3 py-2">
        {{ $t('message.marketingObjects.viewingAppointments.requestedOn', { date: requestedAtFormatted }) }}
      </div>
      <div
        v-if="showScheduleButton || showRequestButton"
        class="row mt-3">
        <div
          v-if="showScheduleButton"
          class="col-12 col-md-4 col-lg-3 mb-2">
          <coozzy-button
            design="green"
            class="w-100"
            @click="openScheduleViewingAppointments">
            {{ $t('message.marketingObjects.viewingAppointments.schedule') }}
          </coozzy-button>
        </div>
        <div
          v-if="showRequestButton"
          class="col-12 col-md-4 col-lg-3 mb-2">
          <coozzy-button
            design="default"
            class="w-100"
            :disabled="requesting"
            @click="openRequestViewingAppointment">
            {{ $t('message.marketingObjects.viewingAppointments.request') }}
          </coozzy-button>
        </div>
      </div>
    </coozzy-card>
    <coozzy-card
      v-if="showAppointmentsTable"
      class="global-card mb-3">
      <viewing-appointments-table
        ref="viewingAppointmentsTable"
        :appointments="appointments"
        :busy="!appointmentsLoaded"
        :viewing-contact-id="viewingContactId"
        :viewing-contact-name="viewingContactDisplayName"
        @row-clicked="onAppointmentRowClicked"
        @cancel-appointments="onCancelAppointments"
        @serial-email="onSerialEmail" />
      <div class="row mt-3">
        <div class="col-12 col-md-4 col-lg-3 mb-2">
          <coozzy-button
            design="green"
            class="w-100"
            @click="openAddViewingAppointments">
            {{ $t('message.marketingObjects.viewingAppointments.addAppointments') }}
          </coozzy-button>
        </div>
        <div class="col-12 col-md-4 col-lg-3 mb-2 ml-md-auto text-md-right">
          <coozzy-button
            design="default"
            class="w-100"
            :disabled="copyingLink"
            @click="copyViewingAppointmentsLink">
            {{ $t('message.marketingObjects.viewingAppointments.copyLink') }}
          </coozzy-button>
        </div>
      </div>
    </coozzy-card>
    <viewing-appointment-participants-modal
      :object-id="object.id"
      :appointment="selectedAppointment"
      @closed="selectedAppointment = null"
      @open-request="$emit('open-request', $event)"
      @participant-unregistered="onParticipantUnregistered"
      @max-participants-updated="onMaxParticipantsUpdated" />
    <b-modal
      id="viewingAppointmentBulkCancelModal"
      ref="viewingAppointmentBulkCancelModal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingObjects.viewingAppointments.cancelParticipant.title')">
      <div class="col pl-0">
        <coozzy-button
          size="small"
          design="default"
          :disabled="cancellingAppointments"
          @click="hideBulkCancelConfirm">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="prop-green"
          :disabled="cancellingAppointments"
          @click="cancelAppointmentsWithoutMail">
          {{ $t('message.generic.rejectWithoutMail') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0 mr-3"
          design="prop-green"
          :disabled="cancellingAppointments || !pendingCancelHasParticipantEmails"
          @click="cancelAppointmentsWithMail">
          {{ $t('message.generic.rejectWithMail') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      id="viewingAppointmentChangeContactConfirm"
      ref="viewingAppointmentChangeContactConfirm"
      no-close-on-backdrop
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer
      @hidden="onChangeContactConfirmHidden">
      <div class="col pl-0">
        <p>{{ $t('message.marketingObjects.viewingAppointments.changeContactConfirm') }}</p>
      </div>
      <div class="col pl-0">
        <coozzy-button
          size="small"
          class="float-left mb-0 border"
          design="transparent"
          @click="cancelContactChange">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          design="green"
          class="float-right ml-3 mb-0"
          @click="confirmContactChange">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapMutations } from 'vuex'
import CoozzyCard from '@/framework/components/card/CoozzyCard'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import ViewingAppointmentContactSection from '@/marketing/components/ViewingAppointmentContactSection'
import ViewingAppointmentsTable from '@/marketing/components/ViewingAppointmentsTable'
import ViewingAppointmentParticipantsModal from '@/marketing/components/ViewingAppointmentParticipantsModal'
import ObjectApi from '@/misc/apis/ObjectApi'
import ContactApi from '@/misc/apis/ContactApi'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import { dateUtils } from '@/mixins/dateUtils'
import { mail } from '@/mixins/mail'
import { getSendableEmailEntries } from '@/misc/utils/contactEmail'
import { buildMailLinkHtml } from '@/misc/utils/mailLinkUtils'
import { user } from '@/mixins/user'

const REQUESTED_STORAGE_PREFIX = 'viewingAppointmentsRequested:'

export default {
  name: 'ViewingAppointmentsTab',
  components: {
    CoozzyButton,
    CoozzyCard,
    ViewingAppointmentContactSection,
    ViewingAppointmentsTable,
    ViewingAppointmentParticipantsModal
  },
  mixins: [dateUtils, mail, user],
  props: {
    object: {
      type: Object,
      required: true
    },
    building: {
      type: Object,
      default: null
    },
    buildingReady: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      localObject: null,
      appointments: [],
      appointmentsLoaded: false,
      saveTimeout: null,
      saving: false,
      saveQueued: false,
      contactsResolved: false,
      contactChangeConfirmReady: false,
      requesting: false,
      copyingLink: false,
      awaitingRequestMailSend: false,
      sentMailsLengthAtAwait: 0,
      pendingRequestLink: '',
      pendingRequestTokenContactId: '',
      requestedAt: null,
      requestedContactId: '',
      changeContactConfirmOpen: false,
      contactChangeInProgress: false,
      pendingContactChange: null,
      selectedAppointment: null,
      viewingContactDisplayName: '',
      pendingCancelAppointments: [],
      cancellingAppointments: false,
      awaitingCancelMailSend: false,
      cancelSentMailsLengthAtAwait: 0,
      pendingCancelAppointmentsForMail: [],
      openingInformContactMail: false
    }
  },
  computed: {
    ...mapGetters('message', ['getSentMails']),
    pendingCancelHasParticipantEmails() {
      return this.collectParticipantsWithEmail(this.pendingCancelAppointments).contactsWithEmail.length > 0
    },
    contactIdAssigneeMarketing() {
      return this.localObject?.contacts?.contactIdAssigneeMarketing || ''
    },
    viewingContactType() {
      return this.localObject?.viewingAppointment?.contactType || 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE'
    },
    viewingContactId() {
      return this.localObject?.contacts?.contactIdViewingAppointment || ''
    },
    isViewingContactSelected() {
      // Touch reactive object fields so this recomputes when contact selection changes.
      const contactId = this.viewingContactId
      const email = this.localObject?.viewingAppointment?.email
      const type = this.viewingContactType
      const section = this.$refs.viewingContactSection
      if (section && this.contactsResolved) {
        return section.isContactSelectionComplete
      }
      if (type === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        return !!(email || '').trim()
      }
      return !!contactId
    },
    hasActiveAppointments() {
      return this.appointments.some(appointment => {
        if (appointment.status === 'APPOINTMENT_STATUS_CANCELLED') {
          return false
        }
        return !this.isAppointmentInPast(appointment)
      })
    },
    showActionButtons() {
      return this.appointmentsLoaded && !this.hasActiveAppointments
    },
    showScheduleButton() {
      return this.showActionButtons
    },
    showRequestButton() {
      return this.showActionButtons &&
        this.viewingContactType !== 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE'
    },
    showAppointmentsTable() {
      return this.appointmentsLoaded && this.appointments.length > 0
    },
    requestedAtFormatted() {
      if (!this.requestedAt) {
        return ''
      }
      return this.objectToStringDate(this.toObject(this.requestedAt))
    }
  },
  watch: {
    object: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal) {
          if (!oldVal || oldVal.id !== newVal.id) {
            this.contactsResolved = false
            this.contactChangeConfirmReady = false
            this.loadRequestedNote(newVal.id)
          }
          this.localObject = { ...newVal }
          if (newVal.contacts) {
            this.localObject.contacts = { ...newVal.contacts }
          }
          if (newVal.viewingAppointment) {
            this.localObject.viewingAppointment = { ...newVal.viewingAppointment }
          }
        }
      }
    },
    getSentMails: {
      deep: true,
      handler(newVal) {
        this.tryCompleteRequestViewingAppointmentMail(newVal)
        this.tryCompleteCancelMailSend(newVal)
      }
    }
  },
  activated() {
    this.loadAppointments()
    if (this.object?.id) {
      this.loadRequestedNote(this.object.id)
    }
    this.tryOpenInformViewingContactMail()
  },
  mounted() {
    this.loadAppointments()
    if (this.object?.id) {
      this.loadRequestedNote(this.object.id)
    }
    this.$root.$on('bv::modal::hidden', this.onSendMailModalHidden)
    this.tryOpenInformViewingContactMail()
  },
  beforeDestroy() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    this.$root.$off('bv::modal::hidden', this.onSendMailModalHidden)
  },
  methods: {
    ...mapMutations('message', [
      'setAdditionalText',
      'setUseBcc',
      'setSendMailSecondaryTitle',
      'setBccReferenceIds',
      'setSendMailContactsWithoutEmail'
    ]),
    onSendMailModalHidden(bvEvent, modalId) {
      if (modalId !== 'modal-send-mail') {
        return
      }
      if (this.awaitingRequestMailSend) {
        // addSentMail mutates the same array reference; the watcher may not run before hide.
        // Match against newly added mails first so a successful send still records "requested on".
        this.$nextTick(() => {
          if (!this.awaitingRequestMailSend) {
            return
          }
          if (this.tryCompleteRequestViewingAppointmentMail(this.getSentMails)) {
            return
          }
          this.clearAwaitingRequestMailSend()
        })
      }
      if (this.awaitingCancelMailSend) {
        this.$nextTick(() => {
          if (!this.awaitingCancelMailSend) {
            return
          }
          if (this.tryCompleteCancelMailSend(this.getSentMails)) {
            return
          }
          this.clearAwaitingCancelMailSend()
        })
      }
    },
    clearAwaitingRequestMailSend() {
      this.awaitingRequestMailSend = false
      this.sentMailsLengthAtAwait = 0
      this.pendingRequestLink = ''
      this.pendingRequestTokenContactId = ''
    },
    tryCompleteRequestViewingAppointmentMail(mails) {
      if (!this.awaitingRequestMailSend || !Array.isArray(mails)) {
        return false
      }
      // Only react to mails added after we opened the request compose flow.
      if (mails.length <= this.sentMailsLengthAtAwait) {
        return false
      }
      const newMails = mails.slice(this.sentMailsLengthAtAwait)
      const link = this.pendingRequestLink
      const matchesRequest = newMails.some(mail => this.isRequestViewingAppointmentSentMail(mail, link))
      if (!matchesRequest) {
        return false
      }
      this.awaitingRequestMailSend = false
      this.sentMailsLengthAtAwait = 0
      this.pendingRequestLink = ''
      this.markViewingAppointmentsRequested()
      return true
    },
    isRequestViewingAppointmentSentMail(mail, link) {
      if (!mail || !link) {
        return false
      }
      // Outbound/sent entries (and drafts noted via addSentMail) may omit inbound.
      // Only exclude clearly inbound messages.
      if (mail.inbound === true) {
        return false
      }
      const html = mail.bodyHtml || ''
      const text = mail.bodyText || ''
      return html.includes(link) || text.includes(link)
    },
    clearAwaitingCancelMailSend() {
      this.awaitingCancelMailSend = false
      this.cancelSentMailsLengthAtAwait = 0
      this.pendingCancelAppointmentsForMail = []
    },
    isCancelMailSent(mail, appointments) {
      if (!mail || mail.inbound === true) {
        return false
      }
      const requestIds = new Set()
      const contactIds = new Set()
      const participantEmails = new Set()
      ;(appointments || []).forEach(appointment => {
        ;(appointment.participants || []).forEach(participant => {
          if (participant.requestId) {
            requestIds.add(participant.requestId)
          }
          const contactId = participant.contactId || participant.id
          if (contactId) {
            contactIds.add(contactId)
          }
          const email = (participant.email || '').trim().toLowerCase()
          if (email) {
            participantEmails.add(email)
          }
        })
      })
      const mailRefIds = mail.referenceIds || []
      if (requestIds.size && mailRefIds.some(id => requestIds.has(id))) {
        return true
      }
      if (contactIds.size && mailRefIds.some(id => contactIds.has(id))) {
        return true
      }
      if (!participantEmails.size) {
        return false
      }
      const recipients = [...(mail.to || []), ...(mail.bcc || [])]
      return recipients.some(recipient => {
        const email = (recipient.email || '').trim().toLowerCase()
        return email && participantEmails.has(email)
      })
    },
    tryCompleteCancelMailSend(mails) {
      if (!this.awaitingCancelMailSend || !Array.isArray(mails)) {
        return false
      }
      if (mails.length <= this.cancelSentMailsLengthAtAwait) {
        return false
      }
      const appointments = this.pendingCancelAppointmentsForMail
      if (!appointments?.length) {
        return false
      }
      const newMails = mails.slice(this.cancelSentMailsLengthAtAwait)
      const mailSent = newMails.some(mail => this.isCancelMailSent(mail, appointments))
      if (!mailSent) {
        return false
      }
      const pendingAppointments = this.pendingCancelAppointmentsForMail
      this.awaitingCancelMailSend = false
      this.cancelSentMailsLengthAtAwait = 0
      this.cancelAppointments(pendingAppointments).then(() => {
        this.pendingCancelAppointmentsForMail = []
      })
      return true
    },
    requestedStorageKey(objectId) {
      return REQUESTED_STORAGE_PREFIX + objectId
    },
    loadRequestedNote(objectId) {
      if (!objectId) {
        this.requestedAt = null
        this.requestedContactId = ''
        return
      }
      try {
        const raw = localStorage.getItem(this.requestedStorageKey(objectId))
        if (!raw) {
          this.requestedAt = null
          this.requestedContactId = ''
          return
        }
        const parsed = JSON.parse(raw)
        this.requestedAt = parsed.requestedAt || null
        this.requestedContactId = parsed.contactId || ''
      } catch (e) {
        console.log(e)
        this.requestedAt = null
        this.requestedContactId = ''
      }
    },
    markViewingAppointmentsRequested() {
      if (!this.object?.id) {
        return
      }
      const requestedAt = new Date().toISOString()
      // Prefer the contact id the public token was minted for (needed to invalidate later).
      const contactId = this.pendingRequestTokenContactId || this.viewingContactId || ''
      this.requestedAt = requestedAt
      this.requestedContactId = contactId
      this.pendingRequestTokenContactId = ''
      try {
        localStorage.setItem(this.requestedStorageKey(this.object.id), JSON.stringify({
          requestedAt,
          contactId
        }))
      } catch (e) {
        console.log(e)
      }
    },
    clearRequestedNote() {
      this.requestedAt = null
      this.requestedContactId = ''
      if (!this.object?.id) {
        return
      }
      try {
        localStorage.removeItem(this.requestedStorageKey(this.object.id))
      } catch (e) {
        console.log(e)
      }
    },
    openScheduleViewingAppointments(addMore = false) {
      const query = {
        objectId: this.object.id,
        fromPage: 'scheduleViewing'
      }
      if (addMore === true) {
        query.addMore = '1'
      }
      this.$router.push({
        name: 'MarketingCalendarOverview',
        params: { viewCalendar: 'week' },
        query
      })
    },
    openAddViewingAppointments() {
      this.openScheduleViewingAppointments(true)
    },
    copyViewingAppointmentsLink() {
      if (this.copyingLink || !this.object?.id) {
        return
      }
      this.copyingLink = true
      CalendarSchedulerApi.getTokenForRegistering(this.object.id)
        .then(response => {
          const link = `${process.env.VUE_APP_BASE_API_URL}/viewing-appointments/${response.token}`
          this.$clipboard(link)
          Vue.toasted.show(this.$t('message.generic.copied'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
        .finally(() => {
          this.copyingLink = false
        })
    },
    onAppointmentRowClicked(appointment) {
      this.selectedAppointment = appointment
    },
    onCancelAppointments(appointments) {
      if (!appointments?.length || this.cancellingAppointments) {
        return
      }
      const hasParticipants = appointments.some(appointment => (appointment.participants || []).length > 0)
      if (!hasParticipants) {
        this.cancelAppointments(appointments)
        return
      }
      this.pendingCancelAppointments = appointments.slice()
      this.$nextTick(() => {
        this.$refs.viewingAppointmentBulkCancelModal?.show()
      })
    },
    hideBulkCancelConfirm() {
      this.$refs.viewingAppointmentBulkCancelModal?.hide()
      this.pendingCancelAppointments = []
    },
    cancelAppointmentsWithoutMail() {
      if (this.cancellingAppointments) {
        return
      }
      this.cancelAppointments(this.pendingCancelAppointments)
        .then(ok => {
          if (ok) {
            this.hideBulkCancelConfirm()
          }
        })
    },
    cancelAppointmentsWithMail() {
      if (this.cancellingAppointments) {
        return
      }
      const appointments = this.pendingCancelAppointments.slice()
      if (!this.collectParticipantsWithEmail(appointments).contactsWithEmail.length) {
        Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
        return
      }
      if (this.$store.getters['user/isDetailMailModalOpen'] === true) {
        return
      }
      this.pendingCancelAppointmentsForMail = appointments
      this.awaitingCancelMailSend = true
      this.cancelSentMailsLengthAtAwait = this.getSentMails.length
      this.hideBulkCancelConfirm()
      if (!this.openParticipantsSerialEmail(appointments)) {
        this.clearAwaitingCancelMailSend()
      }
    },
    cancelAppointments(appointments) {
      const ids = (appointments || []).map(appointment => appointment.id).filter(Boolean)
      if (!ids.length || this.cancellingAppointments) {
        return Promise.resolve(false)
      }
      this.cancellingAppointments = true
      return CalendarSchedulerApi.cancelAppointments(ids)
        .then(() => {
          this.cancellingAppointments = false
          this.$refs.viewingAppointmentsTable?.clearSelection()
          this.loadAppointments()
          return true
        })
        .catch(e => {
          console.log(e)
          this.cancellingAppointments = false
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
          return false
        })
    },
    onSerialEmail(appointments) {
      if (this.openParticipantsSerialEmail(appointments)) {
        this.$refs.viewingAppointmentsTable?.clearSelection()
      }
    },
    collectParticipantsWithEmail(appointments) {
      const contactsWithEmail = []
      const seenEmails = new Set()
      const referenceIdsByContacts = {}
      ;(appointments || []).forEach(appointment => {
        ;(appointment.participants || []).forEach(participant => {
          const email = (participant.email || '').trim()
          if (!email || seenEmails.has(email.toLowerCase())) {
            return
          }
          seenEmails.add(email.toLowerCase())
          const contactId = participant.contactId || participant.id || email
          contactsWithEmail.push({
            id: contactId,
            firstName: participant.firstName || '',
            lastName: participant.lastName || '',
            name: [participant.firstName, participant.lastName].filter(Boolean).join(' ').trim(),
            email
          })
          if (participant.requestId) {
            referenceIdsByContacts[contactId] = [participant.requestId]
          }
        })
      })
      return { contactsWithEmail, referenceIdsByContacts }
    },
    openParticipantsSerialEmail(appointments) {
      const { contactsWithEmail, referenceIdsByContacts } = this.collectParticipantsWithEmail(appointments)
      if (!contactsWithEmail.length) {
        Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
        return false
      }
      if (this.$store.getters['user/isDetailMailModalOpen'] === true) {
        return false
      }
      this.setSendMailContacts(contactsWithEmail)
      this.setSendMailPreselectedContact(contactsWithEmail)
      this.setSendMailContactsWithoutEmail([])
      this.setSendMailEmailToReply(null)
      this.setSendMailReferenceIds([])
      this.setUseBcc(true)
      this.setSendMailSecondaryTitle(true)
      this.setBccReferenceIds(referenceIdsByContacts)
      this.setRenderWithObjectId(this.object.id)
      this.setSubject('')
      this.setAdditionalText('')
      this.$nextTick(() => {
        this.callShowSendMailModal()
      })
      return true
    },
    onParticipantUnregistered({ appointmentId, participantId }) {
      const appointment = this.appointments.find(item => item.id === appointmentId)
      if (appointment) {
        appointment.participants = (appointment.participants || []).filter(p => p.id !== participantId)
        if (this.selectedAppointment?.id === appointmentId) {
          this.selectedAppointment = { ...appointment, participants: appointment.participants.slice() }
        }
      }
      this.loadAppointments()
    },
    onMaxParticipantsUpdated({ appointment }) {
      const index = this.appointments.findIndex(item => item.id === appointment.id)
      if (index >= 0) {
        this.$set(this.appointments, index, appointment)
      }
      if (this.selectedAppointment?.id === appointment.id) {
        this.selectedAppointment = { ...appointment, participants: (appointment.participants || []).slice() }
      }
    },
    resolveViewingContactDisplayName() {
      const viewingAppointment = this.localObject?.viewingAppointment
      if (this.viewingContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        this.viewingContactDisplayName = [viewingAppointment?.firstName, viewingAppointment?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim()
        return
      }
      if (!this.viewingContactId) {
        this.viewingContactDisplayName = ''
        return
      }
      ContactApi.contactResolve([this.viewingContactId])
        .then(response => {
          const contacts = (response.persons || []).concat(response.companies || [])
          const contact = contacts.find(c => c.id === this.viewingContactId) ||
            contacts.find(c => (c.contactPersons || []).some(p => p.id === this.viewingContactId))
          if (!contact) {
            this.viewingContactDisplayName = ''
            return
          }
          if (contact.id === this.viewingContactId) {
            this.viewingContactDisplayName = contact.name ||
              [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
            return
          }
          const person = (contact.contactPersons || []).find(p => p.id === this.viewingContactId)
          this.viewingContactDisplayName = person?.name ||
            [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim() ||
            contact.name || ''
        })
        .catch(e => {
          console.log(e)
          this.viewingContactDisplayName = ''
        })
    },
    openRequestViewingAppointment() {
      if (this.requesting || !this.object?.id) {
        return
      }
      const isFreeText = this.viewingContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT'
      if (isFreeText && !this.localObject?.viewingAppointment?.email) {
        Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
        return
      }
      if (!isFreeText && !this.viewingContactId) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.missingContact'), { type: 'error' })
        return
      }
      this.requesting = true
      // Resolve recipients before minting a token so a missing email cannot invalidate
      // an already-sent public link.
      this.resolveRequestRecipients()
        .then(contactsWithEmail => {
          if (!contactsWithEmail.length) {
            Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
            return null
          }
          return this.resolveTokenContactId().then(contactId => {
            if (!contactId) {
              Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.missingContact'), { type: 'error' })
              return null
            }
            return { contactId, contactsWithEmail }
          })
        })
        .then(prepared => {
          if (!prepared) {
            return null
          }
          return CalendarSchedulerApi.createTokenForRequestingAppointments(this.object.id, prepared.contactId)
            .then(response => ({
              response,
              contactId: prepared.contactId,
              contactsWithEmail: prepared.contactsWithEmail
            }))
        })
        .then(result => {
          if (!result) {
            return
          }
          const link = `${process.env.VUE_APP_BASE_API_URL}/add-viewing-appointments/${result.response.token}`
          this.pendingRequestTokenContactId = result.contactId || ''
          this.pendingRequestLink = link
          this.setSendMailContacts(result.contactsWithEmail)
          this.setSendMailPreselectedContact(result.contactsWithEmail[0])
          this.setSendMailReferenceIds([])
          this.setSendMailEmailToReply(null)
          // Request mail should not have a pre-filled subject (COOZ-15347).
          this.setSubject('')
          this.setAdditionalText(
            this.$t('message.marketingObjects.viewingAppointments.publicForm.linkToForm') + ' ' + buildMailLinkHtml(link)
          )
          this.setRenderWithObjectId(this.object.id)
          this.sentMailsLengthAtAwait = this.getSentMails.length
          this.awaitingRequestMailSend = true
          this.$nextTick(() => {
            this.callShowSendMailModal()
          })
        })
        .catch(e => {
          console.log(e)
          this.clearAwaitingRequestMailSend()
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
        .finally(() => {
          this.requesting = false
        })
    },
    clearInformViewingContactQuery() {
      if (!this.$route.query.informViewingContact) {
        return
      }
      const query = { ...this.$route.query }
      delete query.informViewingContact
      this.$router.replace({ query })
    },
    tryOpenInformViewingContactMail() {
      const flag = this.$route.query.informViewingContact
      if (flag !== '1' && flag !== 'true') {
        return
      }
      if (this.openingInformContactMail || !this.object?.id) {
        return
      }
      this.openingInformContactMail = true
      this.clearInformViewingContactQuery()
      this.resolveRequestRecipients()
        .then(contactsWithEmail => {
          if (!contactsWithEmail.length) {
            Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
            return null
          }
          return this.resolveTokenContactId().then(contactId => {
            if (!contactId) {
              Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.missingContact'), { type: 'error' })
              return null
            }
            return { contactId, contactsWithEmail }
          })
        })
        .then(prepared => {
          if (!prepared) {
            return null
          }
          return CalendarSchedulerApi.createTokenForRequestingAppointments(this.object.id, prepared.contactId)
            .then(response => ({
              response,
              contactsWithEmail: prepared.contactsWithEmail
            }))
        })
        .then(result => {
          if (!result) {
            return
          }
          const link = `${process.env.VUE_APP_BASE_API_URL}/add-viewing-appointments/${result.response.token}`
          this.setSendMailContacts(result.contactsWithEmail)
          this.setSendMailPreselectedContact(result.contactsWithEmail[0])
          this.setSendMailReferenceIds([])
          this.setSendMailEmailToReply(null)
          this.setSubject(this.$t('message.marketingObjects.viewingAppointments.title'))
          this.setAdditionalText(
            this.$t('message.marketingObjects.viewingAppointments.linkToOverview') + ' ' + buildMailLinkHtml(link)
          )
          this.setRenderWithObjectId(this.object.id)
          this.$nextTick(() => {
            this.callShowSendMailModal()
          })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
        .finally(() => {
          this.openingInformContactMail = false
        })
    },
    resolveTokenContactId() {
      if (this.viewingContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        return this.resolveFreeTextTokenContactId()
      }
      return Promise.resolve(this.viewingContactId || '')
    },
    resolveFreeTextTokenContactId() {
      const viewingAppointment = this.localObject?.viewingAppointment
      const email = viewingAppointment?.email
      if (!email || !this.accountId) {
        return Promise.resolve('')
      }
      const normalizedEmail = email.trim().toLowerCase()
      return ContactApi.resolveByMail(this.accountId, [email])
        .then(response => {
          const contacts = (response.persons || []).concat(response.companies || [])
          const match = contacts.find(contact => {
            const contactEmail = (contact.email || '').trim().toLowerCase()
            const workEmail = (contact.workEmail?.email || '').trim().toLowerCase()
            return contactEmail === normalizedEmail || workEmail === normalizedEmail
          })
          if (match?.id) {
            this.persistViewingContactId(match.id)
            return match.id
          }
          return ContactApi.addContact({
            firstName: viewingAppointment?.firstName || '',
            lastName: viewingAppointment?.lastName || '',
            email: email,
            phone: {
              home1: null,
              home2: null,
              work: null,
              mobile: viewingAppointment?.phone || null,
              fax: null
            },
            types: ['NONE'],
            industries: [],
            address: {
              street: '',
              streetNumber: '',
              zip: '',
              city: '',
              country: '',
              region: ''
            }
          }, this.accountId, 'person').then(createResponse => {
            const personId = createResponse.person?.id || ''
            if (personId) {
              this.persistViewingContactId(personId)
            }
            return personId
          })
        })
        .catch(e => {
          console.log(e)
          return ''
        })
    },
    persistViewingContactId(contactId) {
      if (!contactId || !this.localObject || this.viewingContactId === contactId) {
        return
      }
      if (this.localObject.contacts) {
        Vue.set(this.localObject.contacts, 'contactIdViewingAppointment', contactId)
      } else {
        Vue.set(this.localObject, 'contacts', { contactIdViewingAppointment: contactId })
      }
      this.scheduleSave()
    },
    resolveRequestRecipients() {
      const viewingAppointment = this.localObject?.viewingAppointment
      if (this.viewingContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        const freeTextContact = {
          id: this.viewingContactId || 'viewing-appointment-free-text',
          firstName: viewingAppointment?.firstName || '',
          lastName: viewingAppointment?.lastName || '',
          name: [viewingAppointment?.firstName, viewingAppointment?.lastName].filter(Boolean).join(' '),
          email: viewingAppointment?.email || ''
        }
        return Promise.resolve(getSendableEmailEntries(freeTextContact))
      }
      if (!this.viewingContactId) {
        return Promise.resolve([])
      }
      return ContactApi.contactResolve([this.viewingContactId])
        .then(response => {
          const contacts = (response.persons || []).concat(response.companies || [])
          const contact = contacts.find(c => c.id === this.viewingContactId) ||
            contacts.find(c => (c.contactPersons || []).some(p => p.id === this.viewingContactId))
          if (!contact) {
            return []
          }
          if (contact.id === this.viewingContactId) {
            return getSendableEmailEntries(contact)
          }
          const person = (contact.contactPersons || []).find(p => p.id === this.viewingContactId)
          return getSendableEmailEntries(person || contact)
        })
    },
    loadAppointments() {
      if (!this.object?.id) {
        this.appointmentsLoaded = true
        return
      }
      CalendarSchedulerApi.listAppointmentsByObjectId(this.object.id)
        .then(response => {
          this.appointments = response.appointments || []
          this.appointmentsLoaded = true
        })
        .catch(e => {
          console.log(e)
          this.appointments = []
          this.appointmentsLoaded = true
        })
    },
    isAppointmentInPast(appointment) {
      if (!appointment.date || !appointment.endTime) {
        return false
      }
      const dateStr = this.objectToIsoDate(appointment.date)
      if (!dateStr) {
        return false
      }
      const hours = appointment.endTime.hours ?? 0
      const minutes = appointment.endTime.minutes ?? 0
      const seconds = appointment.endTime.seconds ?? 0
      const end = new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`)
      return end.getTime() < Date.now()
    },
    buildContactSnapshot(overrides = {}) {
      const viewingAppointment = this.localObject?.viewingAppointment || {}
      return {
        contactId: overrides.contactId !== undefined ? overrides.contactId : (this.viewingContactId || ''),
        contactPersonId: overrides.contactPersonId || '',
        selectedContactId: overrides.selectedContactId !== undefined
          ? overrides.selectedContactId
          : (this.$refs.viewingContactSection?.selectedViewingContactId || ''),
        viewingAppointment: { ...viewingAppointment },
        freeText: {
          firstName: viewingAppointment.firstName || '',
          lastName: viewingAppointment.lastName || '',
          email: viewingAppointment.email || '',
          phone: viewingAppointment.phone || ''
        }
      }
    },
    shouldConfirmContactChange() {
      return !!this.requestedAt &&
        this.contactChangeConfirmReady &&
        !this.contactChangeInProgress &&
        !this.changeContactConfirmOpen
    },
    canPersistHydratedContactUpdates() {
      return this.contactChangeConfirmReady || this.contactChangeInProgress
    },
    isSameViewingAppointment(oldAppointment, newAppointment) {
      const defaultType = 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE'
      const normalize = (appointment = {}) => ({
        contactType: appointment.contactType || defaultType,
        publishContact: !!appointment.publishContact,
        remark: appointment.remark || '',
        firstName: appointment.firstName || '',
        lastName: appointment.lastName || '',
        email: appointment.email || '',
        phone: appointment.phone || ''
      })
      const previous = normalize(oldAppointment)
      const next = normalize(newAppointment)
      return previous.contactType === next.contactType &&
        previous.publishContact === next.publishContact &&
        previous.remark === next.remark &&
        previous.firstName === next.firstName &&
        previous.lastName === next.lastName &&
        previous.email === next.email &&
        previous.phone === next.phone
    },
    isCompanyParentOfViewingContact(contactId) {
      const section = this.$refs.viewingContactSection
      const personId = this.viewingContactId || section?.selectedViewingContactPersonId || ''
      if (!contactId || !personId || contactId === personId) {
        return false
      }
      const persons = section?.getContactPersons(contactId) || []
      return persons.some(person => person.id === personId)
    },
    openChangeContactConfirm(snapshot) {
      this.pendingContactChange = { snapshot }
      this.changeContactConfirmOpen = true
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
        this.saveTimeout = null
      }
      this.$nextTick(() => {
        this.$refs.viewingAppointmentChangeContactConfirm?.show()
      })
    },
    onContactSelectionChanged(payload) {
      if (!this.contactsResolved || !this.shouldConfirmContactChange()) {
        return
      }
      const oldEffectiveId = payload.oldContactPersonId || payload.oldContactId || ''
      const newEffectiveId = payload.contactPersonId || payload.contactId || ''
      if (oldEffectiveId === newEffectiveId) {
        return
      }
      // Hydration can emit a selection change that still matches the saved contact.
      if (newEffectiveId && newEffectiveId === this.viewingContactId) {
        return
      }
      if (this.isCompanyParentOfViewingContact(newEffectiveId)) {
        return
      }
      this.openChangeContactConfirm(this.buildContactSnapshot({
        contactId: oldEffectiveId,
        selectedContactId: payload.oldContactId || '',
        contactPersonId: payload.oldContactPersonId || ''
      }))
    },
    onViewingAppointmentUpdated(viewingAppointment) {
      if (!this.localObject || !this.contactsResolved || !this.canPersistHydratedContactUpdates()) {
        return
      }
      if (this.changeContactConfirmOpen && !this.contactChangeInProgress) {
        return
      }
      const defaultType = 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE'
      const oldAppointment = this.localObject.viewingAppointment || {}
      const oldType = oldAppointment.contactType || defaultType
      const newType = viewingAppointment.contactType || defaultType
      const typeChanged = oldType !== newType
      const freeTextIdentityChanged =
        newType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT' &&
        (oldAppointment.email || '') !== (viewingAppointment.email || '')

      if (this.shouldConfirmContactChange() && (typeChanged || freeTextIdentityChanged)) {
        // Keep the token contact id in the snapshot so confirm can invalidate even if
        // the section already cleared the dropdown for a type change.
        this.openChangeContactConfirm(this.buildContactSnapshot({
          contactId: this.requestedContactId || this.viewingContactId || ''
        }))
        return
      }

      if (this.isSameViewingAppointment(oldAppointment, viewingAppointment)) {
        return
      }

      Vue.set(this.localObject, 'viewingAppointment', viewingAppointment)
      this.scheduleSave()
    },
    onViewingContactUpdated(contactId) {
      if (!this.localObject || !this.contactsResolved || !this.canPersistHydratedContactUpdates()) {
        return
      }
      if (this.changeContactConfirmOpen && !this.contactChangeInProgress) {
        return
      }
      const selectedPersonId = this.$refs.viewingContactSection?.selectedViewingContactPersonId || ''
      // Hydration emits the company dropdown id while a nested person is selected.
      if (selectedPersonId && contactId !== selectedPersonId) {
        return
      }
      if (contactId === this.viewingContactId) {
        return
      }
      // Company dropdown hydration emits the company id while the saved contact is
      // the nested person. Do not persist that replacement.
      if (this.isCompanyParentOfViewingContact(contactId)) {
        return
      }
      if (this.localObject.contacts) {
        Vue.set(this.localObject.contacts, 'contactIdViewingAppointment', contactId)
      } else {
        Vue.set(this.localObject, 'contacts', { contactIdViewingAppointment: contactId })
      }
      this.resolveViewingContactDisplayName()
      this.scheduleSave()
    },
    cancelContactChange() {
      const pending = this.pendingContactChange
      this.changeContactConfirmOpen = false
      this.$refs.viewingAppointmentChangeContactConfirm?.hide()
      if (!pending?.snapshot) {
        this.pendingContactChange = null
        return
      }
      this.contactChangeInProgress = true
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
        this.saveTimeout = null
      }
      this.$refs.viewingContactSection?.revertContactSelection({
        contactType: pending.snapshot.viewingAppointment?.contactType,
        contactId: pending.snapshot.selectedContactId,
        contactPersonId: pending.snapshot.contactPersonId,
        viewingAppointment: pending.snapshot.viewingAppointment,
        freeText: pending.snapshot.freeText
      })
      if (this.localObject) {
        Vue.set(this.localObject, 'viewingAppointment', { ...pending.snapshot.viewingAppointment })
        if (this.localObject.contacts) {
          Vue.set(this.localObject.contacts, 'contactIdViewingAppointment', pending.snapshot.contactId)
        }
      }
      this.pendingContactChange = null
      this.$nextTick(() => {
        this.contactChangeInProgress = false
      })
    },
    confirmContactChange() {
      const pending = this.pendingContactChange
      // Prefer the contact the request-mail token was issued for — required to invalidate
      // that link when the selection was cleared by a contact-type change.
      const previousContactId = this.requestedContactId || pending?.snapshot?.contactId || ''
      this.$refs.viewingAppointmentChangeContactConfirm?.hide()
      this.clearRequestedNote()
      this.contactChangeInProgress = true
      this.changeContactConfirmOpen = false
      this.pendingContactChange = null

      const section = this.$refs.viewingContactSection
      if (!section || !this.localObject) {
        this.$nextTick(() => {
          this.contactChangeInProgress = false
        })
        return
      }

      Vue.set(this.localObject, 'viewingAppointment', section.viewingAppointmentPayload)
      const newContactId = section.resolvedViewingContactId || ''
      if (this.localObject.contacts) {
        Vue.set(this.localObject.contacts, 'contactIdViewingAppointment', newContactId)
      } else {
        Vue.set(this.localObject, 'contacts', { contactIdViewingAppointment: newContactId })
      }
      this.scheduleSave()

      // Always rotate the previously issued token first so the emailed public link dies,
      // even when the new selection is empty (type change clears the dropdown).
      // Then mint for the new contact when it differs.
      if (previousContactId && this.object?.id) {
        const rotations = [previousContactId]
        if (newContactId && newContactId !== previousContactId) {
          rotations.push(newContactId)
        }
        rotations.reduce((chain, contactId) => {
          return chain.then(() => CalendarSchedulerApi.createTokenForRequestingAppointments(this.object.id, contactId))
        }, Promise.resolve())
          .catch(e => {
            console.log(e)
          })
          .finally(() => {
            this.contactChangeInProgress = false
          })
        return
      }

      this.$nextTick(() => {
        this.contactChangeInProgress = false
      })
    },
    onChangeContactConfirmHidden() {
      if (this.changeContactConfirmOpen && this.pendingContactChange) {
        this.cancelContactChange()
      }
    },
    onContactsResolved() {
      this.contactsResolved = true
      this.resolveViewingContactDisplayName()
      this.$nextTick(() => {
        this.contactChangeConfirmReady = true
      })
    },
    scheduleSave() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }
      this.saveTimeout = setTimeout(() => {
        this.saveObject()
      }, 500)
    },
    saveObject() {
      if (!this.localObject) {
        return
      }
      if (this.saving) {
        this.saveQueued = true
        return
      }
      this.saving = true
      this.saveQueued = false
      const objectToSave = { ...this.localObject }
      if (objectToSave.contacts) {
        objectToSave.contacts = { ...objectToSave.contacts }
      }
      if (objectToSave.viewingAppointment) {
        objectToSave.viewingAppointment = { ...objectToSave.viewingAppointment }
        if (objectToSave.viewingAppointment.contactType !== 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
          objectToSave.viewingAppointment.firstName = ''
          objectToSave.viewingAppointment.lastName = ''
          objectToSave.viewingAppointment.email = ''
          objectToSave.viewingAppointment.phone = ''
        }
      }
      ObjectApi.updateObject(objectToSave)
        .then(response => {
          this.saving = false
          if (this.saveQueued) {
            this.saveObject()
            return
          }
          this.localObject = { ...response.object }
          if (response.object?.contacts) {
            this.localObject.contacts = { ...response.object.contacts }
          }
          if (response.object?.viewingAppointment) {
            this.localObject.viewingAppointment = { ...response.object.viewingAppointment }
          }
          this.$emit('object-updated', response.object)
        })
        .catch(e => {
          console.log(e)
          this.saving = false
          if (this.saveQueued) {
            this.saveObject()
            return
          }
          Vue.toasted.show(this.$t('message.savingErrors.object'), { type: 'error' })
        })
    }
  }
}
</script>

<style scoped>
.viewing-appointments-requested-note {
  background-color: #f2f4f5;
}
</style>
